/**
 * BUILD GOHIGHLEVEL IFRAME SRC WITH TRACKING PARAMETERS
 *
 * CRITICAL: GoHighLevel forms only read query parameters from the iframe src
 * on INITIAL LOAD. If we try to inject params after the form loads, they won't
 * be captured by GHL's Query Key fields.
 *
 * This utility ensures that ALL tracking parameters (fbclid, utm_source, etc.)
 * are included in the iframe src BEFORE the GHL embed script runs.
 *
 * WHY THIS IS NEEDED:
 * - GHL forms read query parameters ONCE when the iframe loads
 * - If params are added later (via DOM manipulation or src update), GHL ignores them
 * - We must build the complete URL with all params BEFORE rendering the iframe
 *
 * WORKFLOW:
 * 1. Read tracking params from URL or localStorage
 * 2. Read quiz data from localStorage
 * 3. Combine all parameters
 * 4. Build complete iframe src URL
 * 5. Render iframe with this URL
 * 6. THEN load GHL embed script
 */

import { getStoredTrackingParams } from './utmTracking'
import { getQuizDataAsURLParams } from './quizDataPersistence'

/**
 * Priority tracking parameters that MUST be captured
 * These map to Query Key fields in GoHighLevel
 *
 * TO ADD MORE TRACKING FIELDS:
 * Simply add the field name to this array and create a matching
 * Query Key field in your GHL form with the same name
 */
const PRIORITY_TRACKING_PARAMS = [
  'fbclid', // Facebook Click ID
  'utm_source', // Traffic source (e.g., facebook, google)
  'utm_medium', // Marketing medium (e.g., cpc, email)
  'utm_campaign', // Campaign name
  'utm_term', // Paid search keywords
  'utm_content', // Ad variation identifier
]

/**
 * Build complete GHL iframe src URL with all parameters
 *
 * This function:
 * 1. Starts with base GHL form URL
 * 2. Adds tracking params (fbclid, utm_*) from URL or localStorage
 * 3. Adds quiz data params
 * 4. Returns complete URL ready to use as iframe src
 *
 * @param baseFormUrl - Base GHL form URL (e.g., https://api.leadconnectorhq.com/widget/form/FORM_ID)
 * @returns Complete iframe src URL with all parameters
 */
export function buildGHLIframeSrc(baseFormUrl: string): string {
  // Start with base URL
  const url = new URL(baseFormUrl)

  // STEP 1: Add tracking parameters (PRIORITY - these go first)
  // These come from either current URL or localStorage
  const trackingParams = getTrackingParamsFromURLOrStorage()

  PRIORITY_TRACKING_PARAMS.forEach((paramName) => {
    const value = trackingParams[paramName]
    if (value) {
      url.searchParams.set(paramName, value)
      console.log(`[GHL Iframe] Added tracking param: ${paramName}=${value}`)
    }
  })

  // STEP 2: Add quiz data parameters
  // These come from localStorage (saved as user completes quiz)
  const quizParams = getQuizDataAsURLParams()

  quizParams.forEach((value, key) => {
    // Only add if not already present (tracking params take priority)
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
      console.log(`[GHL Iframe] Added quiz param: ${key}=${value}`)
    }
  })

  const finalUrl = url.toString()
  console.log('[GHL Iframe] Final iframe src built:', finalUrl)

  return finalUrl
}

/**
 * Get tracking parameters from current URL or localStorage
 *
 * Priority order:
 * 1. Current URL query parameters (if user just clicked an ad)
 * 2. localStorage (if params were captured on previous page)
 *
 * @returns Object with tracking parameter values
 */
function getTrackingParamsFromURLOrStorage(): Record<string, string> {
  const params: Record<string, string> = {}

  // FIRST: Check current URL for fresh tracking params
  const urlParams = new URLSearchParams(window.location.search)

  PRIORITY_TRACKING_PARAMS.forEach((paramName) => {
    const value = urlParams.get(paramName)
    if (value) {
      params[paramName] = value
    }
  })

  // SECOND: Fall back to localStorage if not in URL
  const storedParams = getStoredTrackingParams()

  if (storedParams) {
    PRIORITY_TRACKING_PARAMS.forEach((paramName) => {
      // Only use stored value if we don't have a fresh one from URL
      if (!params[paramName]) {
        const value = storedParams[paramName as keyof typeof storedParams]
        if (value && typeof value === 'string') {
          params[paramName] = value
        }
      }
    })
  }

  return params
}

/**
 * Check if we have any tracking parameters available
 * Useful for conditional logic or debugging
 *
 * @returns true if any tracking params exist, false otherwise
 */
export function hasTrackingParams(): boolean {
  const params = getTrackingParamsFromURLOrStorage()
  return Object.keys(params).length > 0
}

/**
 * Get a preview of all parameters that will be added to iframe
 * Useful for debugging or displaying to user
 *
 * @returns Object with all parameters (tracking + quiz)
 */
export function getIframeParamsPreview(): Record<string, string> {
  const trackingParams = getTrackingParamsFromURLOrStorage()
  const quizParams = getQuizDataAsURLParams()

  const allParams: Record<string, string> = { ...trackingParams }

  quizParams.forEach((value, key) => {
    allParams[key] = value
  })

  return allParams
}
